import {
  Box,
  MenuItem as MuiMenuItem,
  MenuItemProps as MuiMenuItemProps,
} from "@mui/material";
import { forwardRef } from "react";

export type MenuItemSize = "small" | "medium" | "large";

/**
 * 메뉴 아이템 props — `MenuList` 자식으로 사용. 좌/우 슬롯에 아이콘이나 체크박스 등을 끼울 수 있다.
 */
export interface MenuItemProps extends Omit<MuiMenuItemProps, "size"> {
  size?: MenuItemSize;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
  selected?: boolean;
  disabled?: boolean;
}

const MenuItemStyles = {
  sizes: {
    small: {
      height: 28,
      paddingY: 0,
      paddingX: "12px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: "16px",
      letterSpacing: "-0.25px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "8px",
    },
    medium: {
      height: 36,
      paddingY: 0,
      paddingX: "12px",
      borderRadius: "4px",
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "18px",
      letterSpacing: "0.25px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "8px",
    },
    large: {
      height: 44,
      paddingY: 0,
      paddingX: "12px",
      borderRadius: "4px",
      fontSize: "16px",
      fontWeight: 500,
      lineHeight: "20px",
      letterSpacing: "0.15px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "8px",
    },
  },
  colors: {
    default: {
      color: "rgba(0, 0, 0, 0.87)",
      backgroundColor: "transparent",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
      },
      "&.Mui-focusVisible": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
      },
    },
    selected: {
      color: "rgba(29, 116, 255)",
      backgroundColor: "rgba(0, 0, 0, 0.08)",
      "&:hover": {
        backgroundColor: "rgba(29, 116, 255, 0.1)",
      },
      "&.Mui-focusVisible": {
        backgroundColor: "rgba(29, 116, 255, 0.1)",
      },
    },
    disabled: {
      color: "rgba(0, 0, 0, 0.38)",
      backgroundColor: "transparent",
      "&:hover": {
        backgroundColor: "transparent",
      },
      "&.Mui-focusVisible": {
        backgroundColor: "transparent",
      },
    },
  },
  slots: {
    left: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    right: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      marginLeft: "auto",
    },
  },
} as const;

export const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>(
  (
    {
      size = "medium",
      leftSlot,
      rightSlot,
      selected = false,
      disabled = false,
      children,
      sx,
      ...muiProps
    },
    ref
  ) => {
    const sizeSx = MenuItemStyles.sizes[size];
    const colorKey = disabled ? "disabled" : selected ? "selected" : "default";
    const colorSx = MenuItemStyles.colors[colorKey];

    return (
      <MuiMenuItem
        ref={ref}
        selected={selected}
        disabled={disabled}
        sx={{ ...sizeSx, ...colorSx, ...sx }}
        {...muiProps}
      >
        {leftSlot && <Box sx={MenuItemStyles.slots.left}>{leftSlot}</Box>}
        {children}
        {rightSlot && <Box sx={MenuItemStyles.slots.right}>{rightSlot}</Box>}
      </MuiMenuItem>
    );
  }
);

MenuItem.displayName = "MenuItem";
